import { unknownEntries, isUnknownArray, errorMessage } from "../types/data";
import type { Rating } from "../types/rating";
import type { AuthUser } from "../types/profile";
import type { WatchSummary, WatchSeason } from "../utility/watchHistory";
import type { WatchHistoryContextValue } from "./WatchHistoryStateContext";
import type { WatchMutationResult, WatchEntry, WatchStateMetadata, WatchHistoryCache, CachedWatchState, DemoWatchHistory, WatchActivityMetadata } from "./watchHistoryTypes";
import type { FilmMetadata, EntityId } from "../types/media";
import type { DateValue } from "../types/data";
import type { SeasonRating } from "../types/rating";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  where,
  writeBatch
} from "../utility/firestoreGateway";
import { useFirebase } from "./FirebaseContext";
import { useUserRatings } from "./UserRatingsContext";
import {
  DEMO_SESSION_TTL_MS,
  isDemoUserId,
  markDemoActivity
} from "../utility/demoSession";
import {
  getCachedOrFetch,
  writeCachedValue
} from "../utility/localDataCache";
import {
  readIndexedDbValue,
  writeIndexedDbValue
} from "../utility/indexedDbCache";
import {
  MAX_VIEWINGS_PER_TITLE,
  buildSeasonWatchSummary,
  buildVirtualSeasonWatchSummary,
  buildWatchSummary,
  calculateWatchStatistics,
  createVirtualFirstViewing,
  createVirtualSeasonFirstViewing,
  getWatchFilmId,
  getWatchMediaType,
  getWatchSeasonKey,
  getWatchSeasonNumber,
  getWatchTitle,
  mergeRatingsWithWatchSummaries,
  normalizeViewingOrdinals,
  sortViewingEntriesForDisplay,
  toWatchDate
} from "../utility/watchHistory";
import { markActivityLogStale } from "../utility/activityFeedFreshness";
import { WatchHistoryContext } from "./WatchHistoryStateContext";
export { useOptionalWatchHistory, useWatchHistory } from "./WatchHistoryStateContext";

const createLocalId = (prefix = "viewing") => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const WATCH_HISTORY_CACHE_VERSION = 1;
const getWatchHistoryCacheKey = (userId: string) =>
  `watch_history_state_v${WATCH_HISTORY_CACHE_VERSION}_${userId}`;

const getTimestampMillis = (value: DateValue | undefined) => {
  const date = toWatchDate(value);
  const milliseconds = date?.getTime?.() || 0;
  return Number.isFinite(milliseconds) ? milliseconds : 0;
};

const reviveWatchStateActivity = (snapshot: { id: string; data: () => unknown }): CachedWatchState => {
  const value = (snapshot.data() || {}) as { metadata?: WatchStateMetadata; timestamp?: DateValue };
  const metadata = value.metadata || {};
  const filmId = String(metadata.filmId || "");
  const targetType = metadata.targetType || "title";
  const seasonKey = metadata.seasonKey == null ? null : String(metadata.seasonKey);
  const entries = (isUnknownArray(metadata.entries) ? metadata.entries : []).map((entry, index) => ({
    id: String(entry.id || `${snapshot.id}-${index + 1}`),
    filmId,
    title: metadata.filmTitle || "Неизвестный фильм",
    posterPath: metadata.posterPath || "",
    releaseDate: metadata.releaseDate || "",
    mediaType: metadata.mediaType || "movie",
    ordinal: Number(entry.ordinal) || index + 1,
    watchedAt: toWatchDate(entry.watchedAt),
    dateUnknown: Boolean(entry.dateUnknown || !entry.watchedAt),
    source: entry.source || "manual",
    targetType,
    seasonKey,
    seasonNumber: metadata.seasonNumber ?? entry.seasonNumber ?? null,
    seasonName: metadata.seasonName || null,
    episodeNumber: entry.episodeNumber ?? null,
    activityId: entry.activityId || null,
    note: entry.note || null,
    createdAt: toWatchDate(entry.createdAt || value.timestamp),
    updatedAt: toWatchDate(entry.updatedAt || value.timestamp),
    virtual: false
  }));
  return {
    documentId: String(snapshot.id || ""),
    filmId,
    targetType,
    seasonKey,
    metadata,
    entries,
    updatedAtMillis: Math.max(
      getTimestampMillis(metadata.updatedAt),
      getTimestampMillis(value.timestamp)
    )
  };
};

const reviveCachedWatchState = (state: Partial<CachedWatchState> = {}): CachedWatchState => ({
  ...state,
  metadata: state.metadata || {},
  documentId: String(state.documentId || ""),
  filmId: String(state.filmId || state.metadata?.filmId || ""),
  targetType: state.targetType || state.metadata?.targetType || "title",
  seasonKey: state.seasonKey == null
    ? state.metadata?.seasonKey == null ? null : String(state.metadata.seasonKey)
    : String(state.seasonKey),
  entries: (isUnknownArray(state.entries) ? state.entries : [])
    .map(reviveDemoWatchEntry),
  updatedAtMillis: Math.max(0, Number(state.updatedAtMillis) || 0)
});

const getSeasonScopeKey = (filmId: EntityId | null | undefined, seasonKey: EntityId | null | undefined) => `${String(filmId)}:${String(seasonKey)}`;

const findSeasonRating = (rating: Rating | null | undefined, season: WatchSeason) => {
  const seasonRatings = rating?.seasonRatings || {};
  const seasonKey = getWatchSeasonKey(season);
  const seasonNumber = getWatchSeasonNumber(season);
  if (seasonKey && seasonRatings[seasonKey]) {
    return { id: seasonKey, ...seasonRatings[seasonKey] };
  }
  const match = Object.entries(seasonRatings).find(([key, value]) =>
    String(key) === seasonKey ||
    getWatchSeasonKey(value) === seasonKey ||
    (seasonNumber != null && getWatchSeasonNumber(value) === seasonNumber)
  );
  return match ? { id: match[0], ...match[1] } : null;
};

const compactFirestoreValue = <T extends object,>(value: T): T => Object.fromEntries(
  unknownEntries(value).filter(([, fieldValue]) => fieldValue !== undefined)
) as T;

const requireWatchUser = (user: AuthUser | null): AuthUser => {
  if (!user) throw new Error("watch_history_not_owner");
  return user;
};

const createActivityPayload = ({
  user,
  film,
  rating,
  type,
  title,
  description,
  watchedAt,
  metadata = {}
}: { user: AuthUser; film: FilmMetadata; rating: Rating; type: string; title: string; description: string; watchedAt?: DateValue; metadata?: WatchActivityMetadata }) => ({
  actorId: user.uid,
  type,
  title,
  description,
  experience: 0,
  metadata: compactFirestoreValue({
    actorName: user.displayName || user.email?.split("@")[0] || "Пользователь",
    filmId: getWatchFilmId(film) || getWatchFilmId(rating || {}),
    filmTitle: getWatchTitle(film, rating || {}),
    mediaType: getWatchMediaType(film, rating),
    releaseDate:
      film.release_date || film.first_air_date || rating.releaseDate || rating.filmReleaseDate || "",
    rating: rating.userRate ?? null,
    ...metadata
  }),
  timestamp: toWatchDate(watchedAt) || serverTimestamp()
});

const createWatchStateId = ({ filmId, targetType = "title", seasonKey = "" }: { filmId?: EntityId | null; targetType?: string; seasonKey?: string }) =>
  `watch-state-${targetType}-${encodeURIComponent(String(filmId))}${
    targetType === "season" ? `-${encodeURIComponent(String(seasonKey))}` : ""
  }`;

const serializeWatchStateEntry = (entry: WatchEntry, now: Date) => compactFirestoreValue({
  id: String(entry.id),
  ordinal: Number(entry.ordinal) || 1,
  watchedAt: entry.dateUnknown ? null : toWatchDate(entry.watchedAt),
  dateUnknown: Boolean(entry.dateUnknown || !toWatchDate(entry.watchedAt)),
  source: entry.source || "manual",
  seasonNumber: entry.seasonNumber ?? null,
  episodeNumber: entry.episodeNumber ?? null,
  activityId: entry.activityId || null,
  note: entry.note || null,
  createdAt: toWatchDate(entry.createdAt) || now,
  updatedAt: now
});

const createWatchStatePayload = ({
  user,
  film,
  rating,
  entries,
  now,
  targetType = "title",
  season = null
}: { user: AuthUser; film: FilmMetadata; rating: Rating; entries: WatchEntry[]; now: Date; targetType?: string; season?: WatchSeason | null }) => ({
  actorId: user.uid,
  type: "watch_state",
  title: "Viewing history state",
  description: "",
  experience: 0,
  metadata: compactFirestoreValue({
    filmId: getWatchFilmId(film) || getWatchFilmId(rating || {}),
    filmTitle: getWatchTitle(film, rating || {}),
    posterPath:
      film.poster_path || film.posterPath || rating.posterPath || rating.filmPoster || "",
    releaseDate:
      film.release_date || film.first_air_date || rating.releaseDate || rating.filmReleaseDate || "",
    mediaType: getWatchMediaType(film, rating),
    targetType,
    seasonKey: season ? getWatchSeasonKey(season) : null,
    seasonNumber: season ? getWatchSeasonNumber(season) : null,
    seasonName: season?.name || null,
    entries: entries.map((entry) => serializeWatchStateEntry(entry, now)),
    updatedAt: now
  }),
  timestamp: now
});

const DEMO_WATCH_HISTORY_VERSION = 1;
const getDemoWatchHistoryCacheKey = (userId: string) =>
  `demo_watch_history_v${DEMO_WATCH_HISTORY_VERSION}_${userId}`;

const reviveDemoWatchEntry = (entry: WatchEntry): WatchEntry => ({
  ...entry,
  watchedAt: toWatchDate(entry.watchedAt),
  createdAt: toWatchDate(entry.createdAt),
  updatedAt: toWatchDate(entry.updatedAt)
});

const reviveDemoWatchSummary = (summary: WatchSummary = {}): WatchSummary => ({
  ...summary,
  firstKnownAt: toWatchDate(summary.firstKnownAt),
  lastWatchedAt: toWatchDate(summary.lastWatchedAt),
  createdAt: toWatchDate(summary.createdAt),
  updatedAt: toWatchDate(summary.updatedAt),
  virtual: false
});

const emptyDemoWatchHistory = (): DemoWatchHistory => ({
  version: DEMO_WATCH_HISTORY_VERSION,
  titles: [],
  seasons: []
});

const normalizeDemoWatchHistory = (value: Partial<DemoWatchHistory> = {}): DemoWatchHistory => ({
  version: DEMO_WATCH_HISTORY_VERSION,
  titles: (isUnknownArray(value.titles) ? value.titles : [])
    .filter((state) => state?.summary?.filmId)
    .map((state) => ({
      summary: reviveDemoWatchSummary(state.summary),
      entries: (isUnknownArray(state.entries) ? state.entries : [])
        .map(reviveDemoWatchEntry)
    })),
  seasons: (isUnknownArray(value.seasons) ? value.seasons : [])
    .filter((state) => state?.summary?.filmId && state?.summary?.seasonKey)
    .map((state) => ({
      summary: reviveDemoWatchSummary(state.summary),
      entries: (isUnknownArray(state.entries) ? state.entries : [])
        .map(reviveDemoWatchEntry)
    }))
});

export function WatchHistoryProvider({ children, userId, enabled = true }: { children?: React.ReactNode; userId?: string | null; enabled?: boolean }) {
  const [db] = useState(() => getFirestore());
  const { auth } = useFirebase();
  const {
    userId: ratingsUserId,
    ratings,
    loading: ratingsLoading
  } = useUserRatings();
  const [persistedSummaries, setPersistedSummaries] = useState<WatchSummary[]>([]);
  const [entriesByFilmId, setEntriesByFilmId] = useState<Record<string, WatchEntry[]>>({});
  const [persistedSeasonSummaries, setPersistedSeasonSummaries] = useState<WatchSummary[]>([]);
  const [seasonEntriesByScope, setSeasonEntriesByScope] = useState<Record<string, WatchEntry[]>>({});
  // Queued mutations can start before React has rendered the state written by
  // the previous mutation. Keep an immediately updated mirror so a rapid
  // double click never calculates both operations from the same stale entry
  // list and silently loses a viewing.
  const persistedSummariesRef = useRef<WatchSummary[]>([]);
  const entriesByFilmIdRef = useRef<Record<string, WatchEntry[]>>({});
  const persistedSeasonSummariesRef = useRef<WatchSummary[]>([]);
  const seasonEntriesByScopeRef = useRef<Record<string, WatchEntry[]>>({});
  const [loading, setLoading] = useState(Boolean(enabled && userId));
  const [error, setError] = useState<Error | null>(null);
  const operationQueuesRef = useRef(new Map<string, Promise<unknown>>());
  const isDemo = isDemoUserId(userId);
  const isOwner = Boolean(
    userId && auth?.currentUser?.uid && String(userId) === String(auth.currentUser.uid)
  );

  useEffect(() => {
    let active = true;
    setPersistedSummaries([]);
    setEntriesByFilmId({});
    setPersistedSeasonSummaries([]);
    setSeasonEntriesByScope({});
    persistedSummariesRef.current = [];
    entriesByFilmIdRef.current = {};
    persistedSeasonSummariesRef.current = [];
    seasonEntriesByScopeRef.current = {};
    setError(null);

    if (!enabled || !userId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    if (isDemo) {
      getCachedOrFetch(
        getDemoWatchHistoryCacheKey(userId),
        async () => emptyDemoWatchHistory(),
        {
          ttl: DEMO_SESSION_TTL_MS,
          staleTtl: DEMO_SESSION_TTL_MS
        }
      )
        .then((cachedValue) => {
          if (!active) return;
          const cached = normalizeDemoWatchHistory(cachedValue);
          const nextTitleSummaries = cached.titles.map(({ summary }) => summary);
          const nextEntriesByFilmId = Object.fromEntries(
            cached.titles.map(({ summary, entries }) => [
              String(summary.filmId),
              sortViewingEntriesForDisplay(entries)
            ])
          );
          const nextSeasonSummaries = cached.seasons.map(({ summary }) => summary);
          const nextSeasonEntries = Object.fromEntries(
            cached.seasons.map(({ summary, entries }) => [
              getSeasonScopeKey(summary.filmId, summary.seasonKey),
              sortViewingEntriesForDisplay(entries)
            ])
          );

          persistedSummariesRef.current = nextTitleSummaries;
          entriesByFilmIdRef.current = nextEntriesByFilmId;
          persistedSeasonSummariesRef.current = nextSeasonSummaries;
          seasonEntriesByScopeRef.current = nextSeasonEntries;
          setPersistedSummaries(nextTitleSummaries);
          setEntriesByFilmId(nextEntriesByFilmId);
          setPersistedSeasonSummaries(nextSeasonSummaries);
          setSeasonEntriesByScope(nextSeasonEntries);
        })
        .catch((loadError: unknown) => {
          if (!active) return;
          console.warn("Не удалось загрузить локальную историю просмотров демо:", loadError);
          setError(loadError instanceof Error ? loadError : new Error(errorMessage(loadError)));
        })
        .finally(() => {
          if (active) setLoading(false);
        });

      return () => {
        active = false;
      };
    }

    const publishStates = (states: CachedWatchState[]) => {
      if (!active) return;
        const titleStates = states.filter((state) => state.targetType === "title" && state.filmId);
        const seasonStates = states.filter((state) =>
          state.targetType === "season" && state.filmId && state.seasonKey
        );

        const nextEntriesByFilmId: Record<string, WatchEntry[]> = {};
        const nextTitleSummaries = titleStates.map(({ filmId, metadata, entries }) => {
          nextEntriesByFilmId[filmId] = sortViewingEntriesForDisplay(entries);
          return {
            ...buildWatchSummary({
              film: {
                id: filmId,
                title: metadata.filmTitle,
                poster_path: metadata.posterPath,
                release_date: metadata.releaseDate,
                media_type: metadata.mediaType
              },
              entries
            }),
            userId: String(userId),
            filmId,
            virtual: false
          };
        });

        const nextSeasonEntries: Record<string, WatchEntry[]> = {};
        const nextSeasonSummaries = seasonStates.map(({ filmId, seasonKey, metadata, entries }) => {
          const scope = getSeasonScopeKey(filmId, seasonKey);
          nextSeasonEntries[scope] = sortViewingEntriesForDisplay(entries);
          return {
            ...buildSeasonWatchSummary({
              film: {
                id: filmId,
                title: metadata.filmTitle,
                poster_path: metadata.posterPath,
                release_date: metadata.releaseDate,
                media_type: "tv"
              },
              season: {
                id: seasonKey || undefined,
                season_number: metadata.seasonNumber ?? undefined,
                name: metadata.seasonName || `Сезон ${metadata.seasonNumber || ""}`.trim()
              },
              entries
            }),
            userId: String(userId),
            virtual: false
          };
        });

        persistedSummariesRef.current = nextTitleSummaries;
        entriesByFilmIdRef.current = nextEntriesByFilmId;
        persistedSeasonSummariesRef.current = nextSeasonSummaries;
        seasonEntriesByScopeRef.current = nextSeasonEntries;
        setPersistedSummaries(nextTitleSummaries);
        setEntriesByFilmId(nextEntriesByFilmId);
        setPersistedSeasonSummaries(nextSeasonSummaries);
        setSeasonEntriesByScope(nextSeasonEntries);
    };

    const loadWatchHistory = async () => {
      const cacheKey = getWatchHistoryCacheKey(userId);
      let cachedStates: CachedWatchState[] = [];
      let lastServerTimestamp = 0;
      try {
        const cached = await readIndexedDbValue<WatchHistoryCache>(cacheKey);
        if (
          cached?.version === WATCH_HISTORY_CACHE_VERSION &&
          String(cached?.userId || "") === String(userId) &&
          isUnknownArray(cached.states)
        ) {
          cachedStates = cached.states.map(reviveCachedWatchState);
          lastServerTimestamp = Math.max(
            0,
            Number(cached.lastServerTimestamp) || 0
          );
          publishStates(cachedStates);
          // Cached data paints immediately. The small delta query below keeps
          // another device authoritative without re-reading unchanged states.
          if (active) setLoading(false);
        }
      } catch (cacheError) {
        console.warn("Не удалось прочитать кеш истории просмотров:", cacheError);
      }

      try {
        const activitiesRef = collection(
          db,
          "userActivities",
          String(userId),
          "activities"
        );
        const watchStateQuery = lastServerTimestamp > 0
          ? query(
              activitiesRef,
              // Cache timestamps are stored with millisecond precision while
              // Firestore timestamps can contain nanoseconds. Overlap the
              // boundary by one millisecond so two near-simultaneous writes
              // can never make an update disappear between refreshes.
              where(
                "metadata.updatedAt",
                ">",
                new Date(Math.max(0, lastServerTimestamp - 1))
              )
            )
          : query(activitiesRef, where("type", "==", "watch_state"));
        const snapshot = await getDocs(watchStateQuery);
        if (!active) return;

        const changedStates = snapshot.docs
          .filter((documentSnapshot) =>
            documentSnapshot.data()?.type === "watch_state"
          )
          .map(reviveWatchStateActivity);
        const nextStates = lastServerTimestamp > 0
          ? (() => {
              const byId = new Map(cachedStates.map((state) => [
                state.documentId,
                state
              ]));
              changedStates.forEach((state) => byId.set(state.documentId, state));
              return [...byId.values()];
            })()
          : changedStates;
        const nextTimestamp = nextStates.reduce(
          (latest, state) => Math.max(latest, Number(state.updatedAtMillis) || 0),
          lastServerTimestamp
        );

        publishStates(nextStates);
        await writeIndexedDbValue(cacheKey, {
          version: WATCH_HISTORY_CACHE_VERSION,
          userId: String(userId),
          states: nextStates,
          lastServerTimestamp: nextTimestamp
        });
      } catch (loadError) {
        if (!active) return;
        console.warn("Не удалось загрузить историю просмотров:", loadError);
        // A server failure must not erase a usable local snapshot. On the very
        // first load there is no truthful fallback, so expose the error while
        // leaving the provider settled instead of inventing empty remote data.
        setError(loadError instanceof Error ? loadError : new Error(errorMessage(loadError)));
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadWatchHistory();

    return () => {
      active = false;
    };
  }, [db, enabled, isDemo, userId]);

  const summaries = useMemo(
    () => mergeRatingsWithWatchSummaries(
      ratingsUserId === userId ? ratings : [],
      persistedSummaries
    ),
    [persistedSummaries, ratings, ratingsUserId, userId]
  );
  const summariesByFilmId = useMemo(
    () => new Map(summaries.map((summary) => [String(summary.filmId), summary])),
    [summaries]
  );
  const statistics = useMemo(() => calculateWatchStatistics(summaries), [summaries]);
  const seasonSummaries = useMemo(() => {
    const byScope = new Map<string, WatchSummary>();
    persistedSeasonSummaries.forEach((summary) => {
      const scope = getSeasonScopeKey(summary.filmId, summary.seasonKey);
      byScope.set(scope, { ...summary, virtual: false });
    });
    if (ratingsUserId === userId) {
      (ratings || []).forEach((rating) => {
        const filmId = getWatchFilmId(rating || {});
        Object.entries(rating?.seasonRatings || {}).forEach(([seasonKey, seasonRating]) => {
          const scope = getSeasonScopeKey(filmId, seasonKey);
          if (byScope.has(scope) || !Number.isFinite(Number(seasonRating?.userRate))) return;
          const season = { id: seasonKey, ...seasonRating };
          const summary = buildVirtualSeasonWatchSummary(season, rating, season);
          if (summary) byScope.set(scope, summary);
        });
      });
    }
    return [...byScope.values()];
  }, [persistedSeasonSummaries, ratings, ratingsUserId, userId]);
  const seasonSummariesByScope = useMemo(
    () => new Map(seasonSummaries.map((summary) => [
      getSeasonScopeKey(summary.filmId, summary.seasonKey),
      summary
    ])),
    [seasonSummaries]
  );

  const updateLocalSummary = useCallback((summary: WatchSummary, entries?: WatchEntry[]) => {
    const filmId = String(summary.filmId);
    const nextSummaries = [
      ...persistedSummariesRef.current.filter((item) => String(item.filmId) !== filmId),
      { ...summary, filmId, virtual: false }
    ];
    persistedSummariesRef.current = nextSummaries;
    setPersistedSummaries(nextSummaries);
    if (entries) {
      const nextEntries = {
        ...entriesByFilmIdRef.current,
        [filmId]: sortViewingEntriesForDisplay(entries)
      };
      entriesByFilmIdRef.current = nextEntries;
      setEntriesByFilmId(nextEntries);
    }
  }, []);

  const updateLocalSeasonSummary = useCallback((summary: WatchSummary, entries?: WatchEntry[]) => {
    const filmId = String(summary.filmId);
    const seasonKey = String(summary.seasonKey);
    const scope = getSeasonScopeKey(filmId, seasonKey);
    const nextSummaries = [
      ...persistedSeasonSummariesRef.current.filter((item) =>
        getSeasonScopeKey(item.filmId, item.seasonKey) !== scope
      ),
      { ...summary, filmId, seasonKey, virtual: false }
    ];
    persistedSeasonSummariesRef.current = nextSummaries;
    setPersistedSeasonSummaries(nextSummaries);
    if (entries) {
      const nextEntries = {
        ...seasonEntriesByScopeRef.current,
        [scope]: sortViewingEntriesForDisplay(entries)
      };
      seasonEntriesByScopeRef.current = nextEntries;
      setSeasonEntriesByScope(nextEntries);
    }
  }, []);

  const persistDemoWatchHistory = useCallback(() => {
    if (!isDemo || !userId) return;
    writeCachedValue(getDemoWatchHistoryCacheKey(userId), {
      version: DEMO_WATCH_HISTORY_VERSION,
      titles: persistedSummariesRef.current.map((summary) => ({
        summary,
        entries: entriesByFilmIdRef.current[String(summary.filmId)] || []
      })),
      seasons: persistedSeasonSummariesRef.current.map((summary) => ({
        summary,
        entries: seasonEntriesByScopeRef.current[
          getSeasonScopeKey(summary.filmId, summary.seasonKey)
        ] || []
      }))
    });
  }, [isDemo, userId]);

  const getRatingForFilm = useCallback((filmId: EntityId, suppliedRating: Rating | null = null) =>
    suppliedRating || (ratings || []).find((item) =>
      getWatchFilmId(item) === String(filmId)
    ) || null, [ratings]);

  const loadPersistedTitleState = useCallback(async ({ film, rating }: { film: FilmMetadata; rating?: Rating | null }): Promise<WatchMutationResult> => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(rating || {});
    if (!filmId) throw new Error("watch_history_missing_film_id");

    const summary = persistedSummariesRef.current.find(
      (item) => String(item.filmId) === filmId
    ) || null;
    const cachedEntries = entriesByFilmIdRef.current[filmId];
    if (summary && isUnknownArray(cachedEntries)) {
      return { summary, entries: normalizeViewingOrdinals(cachedEntries) };
    }

    const virtualEntry = createVirtualFirstViewing(rating || {}, film);
    return {
      summary,
      entries: virtualEntry ? [virtualEntry] : []
    };
  }, []);

  const loadEntries = useCallback(async (filmOrId: FilmMetadata | EntityId, suppliedRating: Rating | null = null) => {
    const film = typeof filmOrId === "object" ? filmOrId : { id: filmOrId };
    const filmId = getWatchFilmId(film);
    if (!filmId) return [];
    const rating = getRatingForFilm(filmId, suppliedRating);
    const state = await loadPersistedTitleState({ film, rating });
    const entries = sortViewingEntriesForDisplay<WatchEntry>(state.entries);
    setEntriesByFilmId((current) => ({ ...current, [filmId]: entries }));
    return entries;
  }, [getRatingForFilm, loadPersistedTitleState]);

  const loadSeasonSummaries = useCallback(async (filmOrId: FilmMetadata | EntityId, suppliedRating: Rating | null = null) => {
    const film = typeof filmOrId === "object" ? filmOrId : { id: filmOrId };
    const filmId = getWatchFilmId(film);
    if (!filmId) return [];
    const rating = getRatingForFilm(filmId, suppliedRating);

    const persisted = persistedSeasonSummariesRef.current.filter(
      (item) => String(item.filmId) === filmId
    );

    const byScope = new Map(persisted.map((summary) => [
      getSeasonScopeKey(filmId, summary.seasonKey),
      summary
    ]));
    Object.entries(rating?.seasonRatings || {}).forEach(([seasonKey, seasonRating]) => {
      const scope = getSeasonScopeKey(filmId, seasonKey);
      if (byScope.has(scope) || !Number.isFinite(Number(seasonRating?.userRate))) return;
      const season = { id: seasonKey, ...seasonRating };
      const summary = buildVirtualSeasonWatchSummary(season, { ...film, ...rating }, season);
      if (summary) byScope.set(scope, summary);
    });
    return [...byScope.values()];
  }, [getRatingForFilm]);

  const loadPersistedSeasonState = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null }): Promise<WatchMutationResult & { rating: Rating | null; seasonRating: SeasonRating | null }> => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey) throw new Error("watch_history_missing_season_scope");
    const rating = getRatingForFilm(filmId, suppliedRating);
    const seasonRating = suppliedSeasonRating || findSeasonRating(rating, season);
    const scope = getSeasonScopeKey(filmId, seasonKey);

    const summary = persistedSeasonSummariesRef.current.find((item) =>
      getSeasonScopeKey(item.filmId, item.seasonKey) === scope
    ) || null;
    const cachedEntries = seasonEntriesByScopeRef.current[scope];
    if (summary && isUnknownArray(cachedEntries)) {
      return {
        summary,
        entries: normalizeViewingOrdinals(cachedEntries),
        rating,
        seasonRating
      };
    }
    const virtualEntry = createVirtualSeasonFirstViewing(
      seasonRating || {},
      { ...film, id: filmId },
      season
    );
    return {
      summary: null,
      entries: virtualEntry ? [virtualEntry] : [],
      rating,
      seasonRating
    };
  }, [
    getRatingForFilm
  ]);

  const loadSeasonEntries = useCallback(async (
    film: FilmMetadata,
    season: WatchSeason,
    suppliedRating: Rating | null = null,
    suppliedSeasonRating: SeasonRating | null = null
  ) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey) return [];
    const state = await loadPersistedSeasonState({
      film,
      season,
      rating: suppliedRating,
      seasonRating: suppliedSeasonRating
    });
    const entries = sortViewingEntriesForDisplay<WatchEntry>(state.entries);
    const scope = getSeasonScopeKey(filmId, seasonKey);
    setSeasonEntriesByScope((current) => ({ ...current, [scope]: entries }));
    return entries;
  }, [loadPersistedSeasonState]);

  const persistState = useCallback(async ({
    film,
    rating,
    previousSummary,
    entries,
    activity = null,
    deletedActivityIds = []
  }: { film: FilmMetadata; rating?: Rating | null; previousSummary?: WatchSummary | null; entries: WatchEntry[]; activity?: { id: string; payload: ReturnType<typeof createActivityPayload> } | null; deletedActivityIds?: string[]; deletedEntryIds?: string[] }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(rating || {});
    const now = new Date();
    const normalizedEntries = normalizeViewingOrdinals(entries);
    const summary = {
      ...buildWatchSummary({
        film,
        rating: rating || {},
        previous: previousSummary || {},
        entries: normalizedEntries,
        updatedAt: now
      }),
      userId: String(userId),
      filmId,
      virtual: false
    };

    if (!isDemo) {
      const batch = writeBatch(db);
      const watchStateId = createWatchStateId({ filmId, targetType: "title" });
      const persistedState = createWatchStatePayload({
        user: requireWatchUser(auth.currentUser),
        film,
        rating: rating || {},
        entries: normalizedEntries,
        now,
        targetType: "title"
      });
      const authoritativeUpdatedAt = serverTimestamp();
      batch.set(
        doc(db, "userActivities", String(userId), "activities", watchStateId),
        {
          ...persistedState,
          timestamp: authoritativeUpdatedAt,
          metadata: {
            ...persistedState.metadata,
            updatedAt: authoritativeUpdatedAt
          }
        }
      );
      deletedActivityIds.filter(Boolean).forEach((activityId) => {
        batch.delete(doc(db, "userActivities", String(userId), "activities", String(activityId)));
      });
      if (activity) {
        batch.set(
          doc(db, "userActivities", String(userId), "activities", activity.id),
          activity.payload
        );
      }
      await batch.commit();
      markActivityLogStale(String(userId));
    }

    updateLocalSummary(summary, normalizedEntries);
    if (isDemo) {
      persistDemoWatchHistory();
      markDemoActivity(userId || undefined);
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("movie-watch-history-changed", {
        detail: { userId: String(userId), filmId, summary }
      }));
    }
    return { summary, entries: sortViewingEntriesForDisplay(normalizedEntries) };
  }, [auth, db, isDemo, persistDemoWatchHistory, updateLocalSummary, userId]);

  const persistSeasonState = useCallback(async ({
    film,
    season,
    seasonRating,
    previousSummary,
    entries,
    activity = null,
    deletedActivityIds = []
  }: { film: FilmMetadata; season: WatchSeason; seasonRating?: SeasonRating | null; previousSummary?: WatchSummary | null; entries: WatchEntry[]; activity?: { id: string; payload: ReturnType<typeof createActivityPayload> } | null; deletedActivityIds?: string[]; deletedEntryIds?: string[] }) => {
    const filmId = getWatchFilmId(film);
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(seasonRating || {});
    const now = new Date();
    const normalizedEntries = normalizeViewingOrdinals(entries).map((entry) => ({
      ...entry,
      targetType: "season",
      seasonNumber:
        entry.seasonNumber ??
        getWatchSeasonNumber(season) ??
        getWatchSeasonNumber(seasonRating || {}),
      seasonName: entry.seasonName || season.name || seasonRating?.name || null
    }));
    const summary = {
      ...buildSeasonWatchSummary({
        film,
        season,
        seasonRating: seasonRating || {},
        previous: previousSummary || {},
        entries: normalizedEntries,
        updatedAt: now
      }),
      userId: String(userId),
      filmId,
      seasonKey,
      virtual: false
    };

    if (!isDemo) {
      const batch = writeBatch(db);
      const watchStateId = createWatchStateId({ filmId, targetType: "season", seasonKey });
      const persistedState = createWatchStatePayload({
        user: requireWatchUser(auth.currentUser),
        film,
        rating: {},
        entries: normalizedEntries,
        now,
        targetType: "season",
        season: {
          ...season,
          id: seasonKey,
          name: season.name || seasonRating?.name || undefined
        }
      });
      const authoritativeUpdatedAt = serverTimestamp();
      batch.set(
        doc(db, "userActivities", String(userId), "activities", watchStateId),
        {
          ...persistedState,
          timestamp: authoritativeUpdatedAt,
          metadata: {
            ...persistedState.metadata,
            updatedAt: authoritativeUpdatedAt
          }
        }
      );
      deletedActivityIds.filter(Boolean).forEach((activityId) => {
        batch.delete(doc(db, "userActivities", String(userId), "activities", String(activityId)));
      });
      if (activity) {
        batch.set(
          doc(db, "userActivities", String(userId), "activities", activity.id),
          activity.payload
        );
      }
      await batch.commit();
      markActivityLogStale(String(userId));
    }

    updateLocalSeasonSummary(summary, normalizedEntries);
    if (isDemo) {
      persistDemoWatchHistory();
      markDemoActivity(userId || undefined);
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("movie-watch-history-changed", {
        detail: {
          userId: String(userId),
          filmId,
          seasonKey,
          targetType: "season",
          summary
        }
      }));
    }
    return { summary, entries: sortViewingEntriesForDisplay(normalizedEntries) };
  }, [auth, db, isDemo, persistDemoWatchHistory, updateLocalSeasonSummary, userId]);

  const enqueueOperation = useCallback(<T,>(filmId: EntityId, operation: () => Promise<T>) => {
    const key = String(filmId);
    const previous = operationQueuesRef.current.get(key) || Promise.resolve();
    const next = previous.catch(() => undefined).then(operation);
    operationQueuesRef.current.set(key, next);
    return next.finally(() => {
      if (operationQueuesRef.current.get(key) === next) operationQueuesRef.current.delete(key);
    });
  }, []);

  const ensureFirstViewing = useCallback(async ({ film, rating: suppliedRating = null }: { film: FilmMetadata; rating?: Rating | null }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    if (!filmId || !isOwner) return null;
    const rating = getRatingForFilm(filmId, suppliedRating);
    return enqueueOperation(filmId, async () => {
      const state = await loadPersistedTitleState({ film, rating });
      if (state.summary) return state;
      const firstEntry = state.entries[0] || createVirtualFirstViewing(rating || {}, film);
      if (!firstEntry) return null;
      return persistState({
        film,
        rating,
        previousSummary: null,
        entries: [{ ...firstEntry, virtual: false }]
      });
    });
  }, [enqueueOperation, getRatingForFilm, isOwner, loadPersistedTitleState, persistState]);

  const addViewing = useCallback(async ({
    film,
    rating: suppliedRating = null,
    watchedAt = null,
    dateUnknown = false
  }: { film: FilmMetadata; rating?: Rating | null; watchedAt?: DateValue; dateUnknown?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    if (!filmId || !isOwner) throw new Error("watch_history_not_owner");
    const rating = getRatingForFilm(filmId, suppliedRating);
    return enqueueOperation(filmId, async () => {
      const state = await loadPersistedTitleState({ film, rating });
      if (state.entries.length >= MAX_VIEWINGS_PER_TITLE) {
        throw new Error("watch_history_limit_reached");
      }
      const isRewatch = state.entries.length > 0;
      const activityId = createLocalId("rewatch-activity");
      const entry = {
        id: createLocalId("viewing"),
        filmId,
        watchedAt: dateUnknown ? null : toWatchDate(watchedAt) || new Date(),
        dateUnknown: Boolean(dateUnknown),
        source: "manual",
        createdAt: new Date(),
        activityId: isRewatch ? activityId : null
      };
      const entries = normalizeViewingOrdinals([
        ...state.entries.map((item) => ({ ...item, virtual: false })),
        entry
      ]);
      const ordinal = entries.find((item) => item.id === entry.id)?.ordinal || entries.length;
      const title = getWatchTitle(film, rating || {});
      const activity = isRewatch ? {
        id: activityId,
        payload: createActivityPayload({
          user: requireWatchUser(auth.currentUser),
          film,
          rating: rating || {},
          type: "rewatch",
          title: `Пересмотрен фильм «${title}»`,
          description: `Просмотр №${ordinal}`,
          watchedAt: entry.watchedAt,
          metadata: {
            watchEntryId: entry.id,
            watchOrdinal: ordinal,
            totalViews: entries.length,
            dateUnknown: entry.dateUnknown
          }
        })
      } : null;
      return persistState({
        film,
        rating,
        previousSummary: state.summary,
        entries,
        activity
      });
    });
  }, [auth, enqueueOperation, getRatingForFilm, isOwner, loadPersistedTitleState, persistState]);

  const setTotalViewings = useCallback(async ({ film, rating: suppliedRating = null, total }: { film: FilmMetadata; rating?: Rating | null; total?: number | string }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    if (!filmId || !isOwner) throw new Error("watch_history_not_owner");
    const target = Math.floor(Number(total));
    if (!Number.isFinite(target) || target < 1 || target > MAX_VIEWINGS_PER_TITLE) {
      throw new Error("watch_history_invalid_total");
    }
    const rating = getRatingForFilm(filmId, suppliedRating);
    return enqueueOperation(filmId, async () => {
      const state = await loadPersistedTitleState({ film, rating });
      if (target < state.entries.length) throw new Error("watch_history_total_below_current");
      if (target === state.entries.length) return state;
      const missing = Array.from({ length: target - state.entries.length }, () => ({
        id: createLocalId("viewing"),
        filmId,
        watchedAt: null,
        dateUnknown: true,
        source: "manual_count",
        createdAt: new Date()
      }));
      const entries = normalizeViewingOrdinals([
        ...state.entries.map((entry) => ({ ...entry, virtual: false })),
        ...missing
      ]);
      const title = getWatchTitle(film, rating || {});
      const activityId = createLocalId("history-activity");
      return persistState({
        film,
        rating,
        previousSummary: state.summary,
        entries,
        activity: {
          id: activityId,
          payload: createActivityPayload({
            user: requireWatchUser(auth.currentUser),
            film,
            rating: rating || {},
            type: "watch_history",
            title: `Обновлена история просмотров фильма «${title}»`,
            description: `Указано: просмотрено ${target} раз`,
            metadata: { historyAction: "set_total", totalViews: target }
          })
        }
      });
    });
  }, [auth, enqueueOperation, getRatingForFilm, isOwner, loadPersistedTitleState, persistState]);

  const updateViewing = useCallback(async ({
    film,
    rating: suppliedRating = null,
    entryId,
    watchedAt = null,
    dateUnknown = false
  }: { film: FilmMetadata; rating?: Rating | null; entryId?: string | null; watchedAt?: DateValue; dateUnknown?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    if (!filmId || !entryId || !isOwner) throw new Error("watch_history_not_owner");
    const rating = getRatingForFilm(filmId, suppliedRating);
    return enqueueOperation(filmId, async () => {
      const state = await loadPersistedTitleState({ film, rating });
      if (!state.entries.some((entry) => entry.id === entryId)) {
        throw new Error("watch_history_entry_not_found");
      }
      const entries = state.entries.map((entry) => entry.id === entryId ? {
        ...entry,
        virtual: false,
        watchedAt: dateUnknown ? null : toWatchDate(watchedAt),
        dateUnknown: Boolean(dateUnknown)
      } : { ...entry, virtual: false });
      return persistState({
        film,
        rating,
        previousSummary: state.summary,
        entries
      });
    });
  }, [enqueueOperation, getRatingForFilm, isOwner, loadPersistedTitleState, persistState]);

  const deleteViewing = useCallback(async ({
    film,
    rating: suppliedRating = null,
    entryId,
    confirmLast = false
  }: { film: FilmMetadata; rating?: Rating | null; entryId?: string | null; confirmLast?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    if (!filmId || !entryId || !isOwner) throw new Error("watch_history_not_owner");
    const rating = getRatingForFilm(filmId, suppliedRating);
    return enqueueOperation(filmId, async () => {
      const state = await loadPersistedTitleState({ film, rating });
      const removed = state.entries.find((entry) => entry.id === entryId);
      if (!removed) throw new Error("watch_history_entry_not_found");
      if (state.entries.length === 1 && rating && !confirmLast) {
        const error: Error & { code?: string; expectedRevision?: number; actualRevision?: number } = new Error("watch_history_last_viewing_requires_confirmation");
        error.code = "watch_history_last_viewing_requires_confirmation";
        throw error;
      }
      const entries = state.entries
        .filter((entry) => entry.id !== entryId)
        .map((entry) => ({ ...entry, virtual: false }));
      return persistState({
        film,
        rating,
        previousSummary: state.summary,
        entries,
        deletedEntryIds: [entryId],
        deletedActivityIds: removed.activityId ? [removed.activityId] : []
      });
    });
  }, [enqueueOperation, getRatingForFilm, isOwner, loadPersistedTitleState, persistState]);

  const ensureSeasonFirstViewing = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey || !isOwner) return null;
    const scope = `season:${getSeasonScopeKey(filmId, seasonKey)}`;
    return enqueueOperation(scope, async () => {
      const state = await loadPersistedSeasonState({
        film,
        season,
        rating: suppliedRating,
        seasonRating: suppliedSeasonRating
      });
      if (state.summary) return state;
      const firstEntry = state.entries[0] || createVirtualSeasonFirstViewing(
        state.seasonRating || {},
        { ...film, id: filmId },
        season
      );
      if (!firstEntry) return null;
      return persistSeasonState({
        film: { ...film, id: filmId },
        season,
        seasonRating: state.seasonRating,
        previousSummary: null,
        entries: [{ ...firstEntry, virtual: false }]
      });
    });
  }, [enqueueOperation, isOwner, loadPersistedSeasonState, persistSeasonState]);

  const addSeasonViewing = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null,
    watchedAt = null,
    dateUnknown = false
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null; watchedAt?: DateValue; dateUnknown?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey || !isOwner) throw new Error("watch_history_not_owner");
    const scope = `season:${getSeasonScopeKey(filmId, seasonKey)}`;
    return enqueueOperation(scope, async () => {
      const state = await loadPersistedSeasonState({
        film,
        season,
        rating: suppliedRating,
        seasonRating: suppliedSeasonRating
      });
      if (state.entries.length >= MAX_VIEWINGS_PER_TITLE) {
        throw new Error("watch_history_limit_reached");
      }
      const isRewatch = state.entries.length > 0;
      const activityId = createLocalId("season-rewatch-activity");
      const entry = {
        id: createLocalId("season-viewing"),
        filmId,
        seasonKey,
        watchedAt: dateUnknown ? null : toWatchDate(watchedAt) || new Date(),
        dateUnknown: Boolean(dateUnknown),
        source: "manual",
        targetType: "season",
        seasonNumber: getWatchSeasonNumber(season),
        createdAt: new Date(),
        activityId: isRewatch ? activityId : null
      };
      const entries = normalizeViewingOrdinals([
        ...state.entries.map((item) => ({ ...item, virtual: false })),
        entry
      ]);
      const ordinal = entries.find((item) => item.id === entry.id)?.ordinal || entries.length;
      const seasonName = season.name || state.seasonRating?.name || `Сезон ${getWatchSeasonNumber(season) || ""}`.trim();
      const seriesTitle = getWatchTitle(film, state.rating || {});
      const activity = isRewatch ? {
        id: activityId,
        payload: createActivityPayload({
          user: requireWatchUser(auth.currentUser),
          film,
          rating: state.rating || {},
          type: "rewatch",
          title: `Пересмотрен сезон «${seasonName}» сериала «${seriesTitle}»`,
          description: `Просмотр №${ordinal}`,
          watchedAt: entry.watchedAt,
          metadata: {
            targetType: "season",
            seasonKey,
            seasonNumber: getWatchSeasonNumber(season),
            seasonName,
            watchEntryId: entry.id,
            watchOrdinal: ordinal,
            totalViews: entries.length,
            dateUnknown: entry.dateUnknown
          }
        })
      } : null;
      return persistSeasonState({
        film: { ...film, id: filmId },
        season,
        seasonRating: state.seasonRating,
        previousSummary: state.summary,
        entries,
        activity
      });
    });
  }, [auth, enqueueOperation, isOwner, loadPersistedSeasonState, persistSeasonState]);

  const setSeasonTotalViewings = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null,
    total
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null; total?: number | string }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey || !isOwner) throw new Error("watch_history_not_owner");
    const target = Math.floor(Number(total));
    if (!Number.isFinite(target) || target < 1 || target > MAX_VIEWINGS_PER_TITLE) {
      throw new Error("watch_history_invalid_total");
    }
    const scope = `season:${getSeasonScopeKey(filmId, seasonKey)}`;
    return enqueueOperation(scope, async () => {
      const state = await loadPersistedSeasonState({
        film,
        season,
        rating: suppliedRating,
        seasonRating: suppliedSeasonRating
      });
      if (target < state.entries.length) throw new Error("watch_history_total_below_current");
      if (target === state.entries.length) return state;
      const missing = Array.from({ length: target - state.entries.length }, () => ({
        id: createLocalId("season-viewing"),
        filmId,
        seasonKey,
        watchedAt: null,
        dateUnknown: true,
        source: "manual_count",
        targetType: "season",
        seasonNumber: getWatchSeasonNumber(season),
        createdAt: new Date()
      }));
      const entries = normalizeViewingOrdinals([
        ...state.entries.map((entry) => ({ ...entry, virtual: false })),
        ...missing
      ]);
      const seasonName = season.name || state.seasonRating?.name || `Сезон ${getWatchSeasonNumber(season) || ""}`.trim();
      const seriesTitle = getWatchTitle(film, state.rating || {});
      const activityId = createLocalId("season-history-activity");
      return persistSeasonState({
        film: { ...film, id: filmId },
        season,
        seasonRating: state.seasonRating,
        previousSummary: state.summary,
        entries,
        activity: {
          id: activityId,
          payload: createActivityPayload({
            user: requireWatchUser(auth.currentUser),
            film,
            rating: state.rating || {},
            type: "watch_history",
            title: `Обновлена история просмотров сезона «${seasonName}» сериала «${seriesTitle}»`,
            description: `Указано: просмотрено ${target} раз`,
            metadata: {
              targetType: "season",
              seasonKey,
              seasonNumber: getWatchSeasonNumber(season),
              seasonName,
              historyAction: "set_total",
              totalViews: target
            }
          })
        }
      });
    });
  }, [auth, enqueueOperation, isOwner, loadPersistedSeasonState, persistSeasonState]);

  const updateSeasonViewing = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null,
    entryId,
    watchedAt = null,
    dateUnknown = false
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null; entryId?: string | null; watchedAt?: DateValue; dateUnknown?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey || !entryId || !isOwner) throw new Error("watch_history_not_owner");
    const scope = `season:${getSeasonScopeKey(filmId, seasonKey)}`;
    return enqueueOperation(scope, async () => {
      const state = await loadPersistedSeasonState({
        film,
        season,
        rating: suppliedRating,
        seasonRating: suppliedSeasonRating
      });
      if (!state.entries.some((entry) => entry.id === entryId)) {
        throw new Error("watch_history_entry_not_found");
      }
      const entries = state.entries.map((entry) => entry.id === entryId ? {
        ...entry,
        virtual: false,
        watchedAt: dateUnknown ? null : toWatchDate(watchedAt),
        dateUnknown: Boolean(dateUnknown)
      } : { ...entry, virtual: false });
      return persistSeasonState({
        film: { ...film, id: filmId },
        season,
        seasonRating: state.seasonRating,
        previousSummary: state.summary,
        entries
      });
    });
  }, [enqueueOperation, isOwner, loadPersistedSeasonState, persistSeasonState]);

  const deleteSeasonViewing = useCallback(async ({
    film,
    season,
    rating: suppliedRating = null,
    seasonRating: suppliedSeasonRating = null,
    entryId,
    confirmLast = false
  }: { film: FilmMetadata; season: WatchSeason; rating?: Rating | null; seasonRating?: SeasonRating | null; entryId?: string | null; confirmLast?: boolean }) => {
    const filmId = getWatchFilmId(film) || getWatchFilmId(suppliedRating || {});
    const seasonKey = getWatchSeasonKey(season) || getWatchSeasonKey(suppliedSeasonRating || {});
    if (!filmId || !seasonKey || !entryId || !isOwner) throw new Error("watch_history_not_owner");
    const scope = `season:${getSeasonScopeKey(filmId, seasonKey)}`;
    return enqueueOperation(scope, async () => {
      const state = await loadPersistedSeasonState({
        film,
        season,
        rating: suppliedRating,
        seasonRating: suppliedSeasonRating
      });
      const removed = state.entries.find((entry) => entry.id === entryId);
      if (!removed) throw new Error("watch_history_entry_not_found");
      if (state.entries.length === 1 && state.seasonRating && !confirmLast) {
        const error: Error & { code?: string; expectedRevision?: number; actualRevision?: number } = new Error("watch_history_last_viewing_requires_confirmation");
        error.code = "watch_history_last_viewing_requires_confirmation";
        throw error;
      }
      const entries = state.entries
        .filter((entry) => entry.id !== entryId)
        .map((entry) => ({ ...entry, virtual: false }));
      return persistSeasonState({
        film: { ...film, id: filmId },
        season,
        seasonRating: state.seasonRating,
        previousSummary: state.summary,
        entries,
        deletedEntryIds: [entryId],
        deletedActivityIds: removed.activityId ? [removed.activityId] : []
      });
    });
  }, [enqueueOperation, isOwner, loadPersistedSeasonState, persistSeasonState]);

  useEffect(() => {
    if (!isOwner || typeof window === "undefined") return undefined;
    const handleRatingCreated = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string; rating?: Rating; ratingCreated?: boolean }>).detail || {};
      if (
        String(detail.userId || "") !== String(userId) ||
        !detail.rating
      ) return;
      const changedRating = detail.rating;
      if (detail.ratingCreated === true) {
        void ensureFirstViewing({ film: changedRating, rating: changedRating }).catch((ensureError: unknown) => {
          console.warn("Не удалось материализовать первый просмотр:", ensureError);
        });
      }
      Object.entries(detail.rating.seasonRatings || {}).forEach(([seasonKey, seasonRating]) => {
        if (!Number.isFinite(Number(seasonRating?.userRate))) return;
        const season = { id: seasonKey, ...seasonRating };
        void ensureSeasonFirstViewing({
          film: changedRating,
          season,
          rating: changedRating,
          seasonRating: season
        }).catch((ensureError: unknown) => {
          console.warn("Не удалось материализовать первый просмотр сезона:", ensureError);
        });
      });
    };
    window.addEventListener("movie-film-user-state-changed", handleRatingCreated);
    return () => window.removeEventListener("movie-film-user-state-changed", handleRatingCreated);
  }, [ensureFirstViewing, ensureSeasonFirstViewing, isOwner, userId]);

  const value = useMemo<WatchHistoryContextValue>(() => ({
    userId,
    ratings: ratingsUserId === userId ? ratings : [],
    summaries,
    summariesByFilmId,
    statistics,
    entriesByFilmId,
    seasonSummaries,
    seasonSummariesByScope,
    seasonEntriesByScope,
    loading: Boolean(loading || (ratingsUserId === userId && ratingsLoading)),
    error,
    isOwner,
    getSummary: (filmId) => summariesByFilmId.get(String(filmId)) || null,
    getSeasonSummary: (filmId, seasonOrKey) => seasonSummariesByScope.get(
      getSeasonScopeKey(filmId, getWatchSeasonKey(
        typeof seasonOrKey === "object" ? seasonOrKey : { id: seasonOrKey }
      ))
    ) || null,
    loadEntries,
    loadSeasonSummaries,
    loadSeasonEntries,
    ensureFirstViewing,
    addViewing,
    setTotalViewings,
    updateViewing,
    deleteViewing,
    ensureSeasonFirstViewing,
    addSeasonViewing,
    setSeasonTotalViewings,
    updateSeasonViewing,
    deleteSeasonViewing
  }), [
    addViewing,
    addSeasonViewing,
    deleteViewing,
    deleteSeasonViewing,
    ensureFirstViewing,
    ensureSeasonFirstViewing,
    entriesByFilmId,
    error,
    isOwner,
    loadEntries,
    loadSeasonEntries,
    loadSeasonSummaries,
    loading,
    ratings,
    ratingsLoading,
    ratingsUserId,
    setTotalViewings,
    setSeasonTotalViewings,
    seasonEntriesByScope,
    seasonSummaries,
    seasonSummariesByScope,
    statistics,
    summaries,
    summariesByFilmId,
    updateViewing,
    updateSeasonViewing,
    userId
  ]);

  return (
    <WatchHistoryContext.Provider value={value}>
      {children}
    </WatchHistoryContext.Provider>
  );
}
